import { useMemo, useContext } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { DynamicGridContext } from '~/app/components/dynamic-grid/dynamic-grid-context';
import type { RootState } from '../store';

const dynamicGridSelector = ({ builder }: RootState) => ({
  componentPath: builder.dynamicGrid?.componentPath,
  componentType: builder.dynamicGrid?.componentType,
  disableGridOutline: builder.dynamicGrid?.disableGridOutline,
  marketplaceOffers: builder.dynamicGrid?.marketplaceOffers,
  pressReleases: (builder.dynamicGrid as any)?.pressReleases,
  activeElementId: builder.dynamicGrid?.activeElementId,
});

// Custom hook to safely get context
const useSafeDynamicGridContext = () => {
  return useContext(DynamicGridContext);
};

export const useDynamicGrid = ({
  componentId,
}: { componentId?: string } = {}) => {
  const state = useSelector(dynamicGridSelector, shallowEqual);

  // Always call the hook, but handle the case where context is not available
  const contextComponentId = useSafeDynamicGridContext();

  // Use provided componentId first, then fall back to context
  const resolvedComponentId = componentId || contextComponentId;

  const marketplaceOffers = useMemo(() => {
    if (!resolvedComponentId || !state.marketplaceOffers) {
      return [];
    }
    const offersByComponent = state.marketplaceOffers as Record<string, any[]>;
    return offersByComponent[resolvedComponentId] || [];
  }, [resolvedComponentId, state.marketplaceOffers]);

  return {
    ...state,
    marketplaceOffers,
    pressReleases: state.pressReleases,
  };
};
